import { useCallback } from 'react';

/**
 * A stateless hook that provides memoized functions to manage a list,
 * similar to the functional update form of React's `useState`.
 * @param onChange The state setter function (like `setItems`) from a `useState` hook.
 */
export function useListManager<T>(onChange: (updater: (prevItems: T[]) => T[]) => void) {

  const addItem = useCallback((newItemFactory: () => T) => {
    onChange(prevItems => {
      return [...prevItems, newItemFactory()];
    });
  }, [onChange]);

  const updateItem = useCallback((index: number, updatedFields: Partial<T>) => {
    onChange(prevItems => {
      const newItems = [...prevItems];
      if (newItems[index]) {
        newItems[index] = { ...newItems[index], ...updatedFields };
      }
      return newItems;
    });
  }, [onChange]);

  const deleteItem = useCallback((index: number) => {
    onChange(prevItems => {
      return (prevItems ?? []).filter((_, i) => i !== index);
    });
  }, [onChange]);

  return {
    addItem,
    updateItem,
    deleteItem,
  };
}